import type { ReactNode } from 'react'

import Avatar from './Avatar'
import Rank from './Rank'

type TeamEntry = {
  rank?: number | null
  score: number
  strokesNet: number
  points: number
  holesRegistred: number
  team: { id: string; name?: string | null }
}

const TeamRow = ({
  entry,
  holesTotal = 18,
  isYou,
  footer,
  isNet,
  netAs = 'to-par',
  onClick,
}: {
  entry: TeamEntry
  /**
   * Hole-scores the format expects, when that is not one per hole. A split
   * round's singles block is played 1v1 and 1v1, so both partners' balls
   * count and a pair records 24 over 18 holes — "24/18" would read as an
   * error rather than a complete card.
   */
  holesTotal?: number
  /** True when the auth'd player is on this team — green-50 highlight. */
  isYou: boolean
  isNet: boolean
  /**
   * Which net figure the board ranks on. To-par is the golfer's reading and
   * the default, but it only holds while the card is one score per hole: a
   * split round counts both partners over its singles etappe, so its 24 scores
   * measured against 18 pars produce a number that looks like a round score
   * (–31) and is not one. Those boards show the net total instead.
   */
  netAs?: 'to-par' | 'strokes'
  /** Tap-the-row opens the detail sheet — whole row is the hit target. */
  onClick?: () => void
  /**
   * Rendered under the row, INSIDE it. A split round's block breakdown belongs
   * to the team above it, so as a sibling <li> the separator fell between a
   * team and its own parts and the "you" tint stopped at the button.
   */
  footer?: ReactNode
}) => {
  const netFigure = netAs === 'strokes' ? entry.strokesNet : entry.score
  return (
    <li
      className={`border-b border-ink-100 last:border-b-0 ${
        isYou ? 'bg-green-50' : ''
      }`}
    >
      <button
        type="button"
        onClick={onClick}
        className="w-full flex items-center gap-3 py-3 px-4 text-left active:bg-ink-100 transition-colors"
      >
        <Rank rank={entry.rank} />
        <Avatar name={entry.team.name ?? '—'} size={32} />
        <div className="flex-1 text-sm font-semibold text-ink-900 truncate">
          {entry.team.name ?? 'Uten navn'}
          {isYou ? (
            <span className="ml-2 text-xs font-bold text-green-700">
              · Ditt lag
            </span>
          ) : null}
        </div>
        <div className="text-right">
          <div className="font-mono num font-bold text-ink-900">
            {isNet ? netFigure : entry.points}
          </div>
          <div className="text-[10px] text-ink-500 num">
            {entry.holesRegistred}/{holesTotal}
          </div>
        </div>
      </button>
      {footer}
    </li>
  )
}

export default TeamRow
